import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import toast from 'react-hot-toast'
import axios from 'axios'
import { socketService } from '../../services/socket'
import { checkInStateManager } from '../../services/checkInStateManager'

// const API_BASE_URL = 'http://localhost:17000/api/app'
const API_BASE_URL = 'https://app.connecttechusa.com/api/app'

interface User {
  id: string
  email: string
  name: string
  pictureUrl?: string
  isVerified: boolean
  userOrganizations?: Array<{
    id: string
    role: string
    status: string
    organization: {
      id: string
      name: string
      logoUrl?: string
    }
  }>
  organizations?: Array<{
    id: string
    name: string
    role: string
    status: string 
  }>
  currentOrganization?: {
    id: string
    name: string
    role: string
    logoUrl?: string
  }
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string, name: string) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  acceptInvitation: (token: string, userData: { name: string; password: string }) => Promise<void>
  refreshUser: () => Promise<void>
  selectOrganization: (organizationId: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for stored JWT token on app load
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (token) {
          // Verify token with backend
          const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          setUser(response.data.data.user)
          
          // Connect to WebSocket if user is authenticated
          socketService.connect(token)
          
          // Initialize check-in state manager for the user
          if (response.data.data.user?.id) {
            checkInStateManager.handleLogin(response.data.data.user.id)
          }
        }
      } catch (error) {
        console.error('Token verification failed:', error)
        localStorage.removeItem('auth_token')
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  // Add real-time organization updates
  useEffect(() => {
    // Handle organization updates from WebSocket
    const handleOrganizationUpdate = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('🏢 Organization updated via WebSocket:', customEvent.detail)
      // Refresh user data to get updated organization info
      await refreshUser()
    }

    // Handle user removal from organization
    const handleUserRemoved = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('🚨 User removed from organization:', customEvent.detail)
      // Refresh user data which will handle logout/switch logic
      await refreshUser()
    }

    // Listen for organization update events
    window.addEventListener('organization:updated', handleOrganizationUpdate)
    window.addEventListener('user:removed_from_organization', handleUserRemoved)

    // Cleanup event listeners
    return () => {
      window.removeEventListener('organization:updated', handleOrganizationUpdate)
      window.removeEventListener('user:removed_from_organization', handleUserRemoved)
    }
  }, []) // Note: Empty dependency array to avoid refreshUser dependency issues

  const login = async (email: string, password: string) => {
    try {
      setLoading(true)
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password
      })
      const { data } = response.data
      const { token } = data
      localStorage.setItem('auth_token', token)
      // Instead of setUser(userData), fetch the latest user profile from backend
      await refreshUser()
      // Connect to WebSocket after successful login
      socketService.connect(token)
      
      // Initialize check-in state manager for the logged-in user
      if (data.user?.id) {
        checkInStateManager.handleLogin(data.user.id)
      }
      
      toast.success('Welcome back!')
    } catch (error: any) {
      console.error('Login error:', error)
      toast.error(error.response?.data?.message || 'Failed to sign in')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signup = async (email: string, password: string, name: string) => {
    try {
      setLoading(true)
      await axios.post(`${API_BASE_URL}/auth/signup`, {
        email,
        password,
        name
      })
      
      toast.success('Account created! Please check your email to verify your account.')
    } catch (error: any) {
      console.error('Signup error:', error)
      toast.error(error.response?.data?.message || 'Failed to create account')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      // Disconnect WebSocket before logout
      socketService.disconnect()
      
      // Clear check-in state
      checkInStateManager.handleLogout()
      
      localStorage.removeItem('auth_token')
      setUser(null)
      toast.success('Signed out successfully')
    } catch (error: any) {
      console.error('Logout error:', error)
      toast.error('Failed to sign out')
    }
  }

  const resetPassword = async (email: string) => {
    try {
      await axios.post(`${API_BASE_URL}/auth/reset-password`, { email })
      toast.success('Password reset email sent! Check your inbox.')
    } catch (error: any) {
      console.error('Password reset error:', error)
      toast.error(error.response?.data?.message || 'Failed to send password reset email')
      throw error
    }
  }

  const acceptInvitation = async (token: string, userData: { name: string; password: string }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/accept-invitation`, {
        token,
        ...userData
      })
      toast.success('Invitation accepted successfully!')
      return response.data
    } catch (error: any) {
      console.error('Accept invitation error:', error)
      toast.error(error.response?.data?.message || 'Failed to accept invitation')
      throw error
    }
  }

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        setUser(null)
        return
      }
      const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      console.log('👤 User data:', response.data.data.user)
      const userData = response.data.data.user
      
      // Check if server returned a new token due to stale organization
      if (userData.newToken) {
        console.log('🔄 Server provided new token due to organization change')
        localStorage.setItem('auth_token', userData.newToken)
        toast.success(`Switched to ${userData.currentOrganization?.name || 'available organization'}`)
      }
      
      // Check if user has no organizations - log them out
      if (!userData.organizations || userData.organizations.length === 0) {
        console.log('🚨 User has no organizations, logging out...')
        await logout()
        return
      }
      
      // If user has organizations but no current organization, switch to the first available
      if (!userData.currentOrganization && userData.organizations.length > 0) {
        console.log('🔄 No current organization, switching to first available...')
        const firstOrg = userData.organizations.find((org: any) => org.status === 'ACCEPTED')
        if (firstOrg) {
          await selectOrganization(firstOrg.id)
          return
        }
      }
      
      setUser(userData)
      socketService.connect(userData.newToken || token)
      
      // Initialize check-in state manager for the user
      if (userData?.id) {
        checkInStateManager.handleLogin(userData.id)
      }
    } catch (error) {
      console.error('Failed to refresh user:', error)
      localStorage.removeItem('auth_token')
      setUser(null)
    }
  }

  const selectOrganization = async (organizationId: string) => {
    try {
      console.log('🔄 Switching to organization:', organizationId)
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('No authentication token')
      }

      const response = await axios.post(`${API_BASE_URL}/auth/select-organization`, {
        organizationId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      console.log('📊 Organization switch response:', response.data)

      // Update the stored token with the new one
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token)
        console.log('🔐 New token stored with organization context')

        // Refresh user data with new organization context
        await refreshUser()
        console.log('🔄 User data refreshed with new organization')
        
        toast.success(`Switched to ${response.data.organization.name}`)
      }
    } catch (error: any) {
      console.error('Organization switch error:', error)
      toast.error(error.response?.data?.error || 'Failed to switch organization')
      throw error
    }
  }

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    resetPassword,
    acceptInvitation,
    refreshUser,
    selectOrganization,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 